import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@myua/kit';
import { filterConditionApplied } from '../../../../../../general/modules/filter/actions';
import { EnumTuner } from '../../../../../../general/modules/filter/components/Tuner/EnumTuner';
import { Type } from '../../../../../../general/modules/filter/dataModel';
import { FMColumn } from '../../../model';
import { getActiveFilters, getGenres } from '../../../selectors/selectors';
import { Label } from './Label';

/**
 * Фильтр для колонки Genre. В отличие от универсального `FilterEnumContainer`,
 * берёт опции из полного справочника жанров проекта (`fileManager.genres`),
 * а не из уникальных значений текущей выборки. Это необходимо, потому что
 * видимые строки могут быть отфильтрованы/пагинированы, но пользователь
 * должен иметь возможность выбрать любой жанр проекта.
 */
export function FilterGenresContainer({ column }: { column: FMColumn<Type.ID> }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const currentActiveFilter = useSelector(getActiveFilters)[column.key];
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const genres = useSelector(getGenres);
  const dispatch = useDispatch();

  const onCancel = useMemo(
    () => () => {
      const en = (currentActiveFilter?.enum ?? []) as number[];
      setChecked(new Set(en));
      setOpen(false);
      setSearch('');
    },
    [currentActiveFilter],
  );

  useEffect(() => {
    onCancel();
  }, [onCancel]);

  const values = useMemo(() => {
    const options = genres
      .map((g) => ({
        id: g.id,
        title: g.name ?? '<empty>',
      }))
      .filter((o) => o.title.toLowerCase().includes(search.toLowerCase()));

    options.sort((a, b) => a.title.localeCompare(b.title));

    const initialChecked = new Set((currentActiveFilter?.enum ?? []) as number[]);
    return [
      ...options.filter(({ id }) => initialChecked.has(id)),
      ...options.filter(({ id }) => !initialChecked.has(id)),
    ];
  }, [genres, search, currentActiveFilter]);

  const onApply = useCallback(() => {
    const existingIds = new Set(genres.map((g) => g.id));
    const ids = Array.from(checked.keys()).filter((id) => existingIds.has(id));
    dispatch(filterConditionApplied({ fieldID: column.key, condition: { enum: ids } }));
    setOpen(false);
    setSearch('');
  }, [checked, column, dispatch, genres]);

  const checkedItems = useMemo(() => {
    const keys = Array.from(checked.keys());
    return Object.fromEntries(keys.map((k) => [k, true]));
  }, [checked]);

  return (
    <div
      className="ch-file-manager__filter"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Popover
        trigger="click"
        placement="bottomRight"
        // Semantic звал onOpen и onClose раздельно; у antd одна ручка,
        // закрытие по-прежнему означает отмену правки фильтра.
        onOpenChange={(isOpen) => (isOpen ? setOpen(true) : onCancel())}
        open={open}
        // Закрытый поповер не держим смонтированным: под display: none
        // CellMeasurer в EnumTuner меряет строки нулевой высотой, и при
        // следующем открытии пункты списка слипаются.
        destroyOnHidden
        content={
          <EnumTuner
            searchResultItems={values}
            applyOnClick={onApply}
            checkedItems={checkedItems}
            closeOnClick={onCancel}
            isSingle={false}
            itemOnSwitch={() => {
              // not unexpected empty arrow func
            }}
            itemsOnChange={(ids: string[], v: boolean) => {
              setChecked((old) => {
                const newChecked = new Set(old);
                if (v) {
                  ids.forEach((id) => newChecked.add(Number(id)));
                } else {
                  ids.forEach((id) => newChecked.delete(Number(id)));
                }
                return newChecked;
              });
            }}
            searchOnChange={(e) => {
              setSearch(e.target.value);
            }}
            searchText={search}
          />
        }
      >
        <Label columnKey={column.key} />
      </Popover>
    </div>
  );
}
